import { EntityCommonInfoModel, FilteredPagedQueryResult, FilteredPagedModel, CommonMultiDeleteModel } from "../../entities/common";
import { apiSlice, TagTypes } from "./apiSlice";
import { getQuerySearchParams } from "../../utils";
import { PcRawMaterialModel } from "../../entities/pcRawMaterial";

export const pcRawMaterialApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getPcRawMaterialCommonInfo: builder.query<EntityCommonInfoModel, boolean>({
      query: (isEstimated) => `/pcRawMaterial/${isEstimated}/commonInfo`,
      providesTags: [{ type: TagTypes.PcRawMaterial, id: "LIST" }],
    }),
    getPcRawMaterialFilteredPaged: builder.query<FilteredPagedQueryResult<PcRawMaterialModel>, FilteredPagedModel>({
      query: ({ isEstimated, page = 1, pageSize = 10, searchTerm = "", sortFieldName = "", descending = false, ...rest }: FilteredPagedModel) => {
        let url = `/pcRawMaterial/${isEstimated}/pageable?page=${page}&pageSize=${pageSize}&searchTerm=${searchTerm}&sortFieldName=${sortFieldName}&descending=${descending}`;
        const restParams = getQuerySearchParams(rest);
        if (restParams) {
          url += `&${restParams}`;
        }

        return url;
      },
      providesTags: (result, error, arg) =>
        result
          ? [
              { type: TagTypes.PcRawMaterial, id: "PARTIAL-LIST" },
              ...result.data.map(({ id }: { id: number | string }) => ({
                type: TagTypes.PcRawMaterial,
                id,
              })),
            ]
          : [{ type: TagTypes.PcRawMaterial, id: "PARTIAL-LIST" }],
    }),
    getPcRawMaterial: builder.query<PcRawMaterialModel, number>({
      query: (pcRawMaterialId) => `/pcRawMaterial/${pcRawMaterialId}`,
      providesTags: (result, error, arg) => [{ type: TagTypes.PcRawMaterial, id: arg }],
    }),
    scrapPcRawMaterial: builder.query<PcRawMaterialModel, { isEstimated: boolean; tracingNumber: string }>({
      query: ({ isEstimated, tracingNumber }) => ({
        url: `/pcRawMaterial/scrap/${isEstimated}`,
        method: "POST",
        body: { tracingNumber },
      }),
      providesTags: [{ type: TagTypes.PcRawMaterial, id: "Scrap" }],
    }),
    addPcRawMaterial: builder.mutation<PcRawMaterialModel, PcRawMaterialModel>({
      query: (initialPcRawMaterial) => ({
        url: "/pcRawMaterial",
        method: "POST",
        body: initialPcRawMaterial,
      }),
      invalidatesTags: [
        { type: TagTypes.PcRawMaterial, id: "LIST" },
        { type: TagTypes.PcRawMaterial, id: "PARTIAL-LIST" },
      ],
    }),
    editPcRawMaterial: builder.mutation<PcRawMaterialModel, PcRawMaterialModel>({
      query: (pcRawMaterial) => ({
        url: `/pcRawMaterial/${pcRawMaterial.id}`,
        method: "PUT",
        body: pcRawMaterial,
      }),
      invalidatesTags: (result, error, arg) => [{ type: TagTypes.PcRawMaterial, id: arg.id }],
    }),
    deletePcRawMaterial: builder.mutation({
      query: (pcRawMaterialId: number) => ({
        url: `/pcRawMaterial/${pcRawMaterialId}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, arg) =>
        error
          ? []
          : [
              { type: TagTypes.PcRawMaterial, id: arg },
              { type: TagTypes.PcRawMaterial, id: "LIST" },
            ],
    }),
    deleteMultiPcRawMaterials: builder.mutation({
      query: (multiDeleteModel: CommonMultiDeleteModel) => ({
        url: "/pcRawMaterial/multiDelete",
        method: "DELETE",
        body: multiDeleteModel,
      }),
      invalidatesTags: (result, error, arg) =>
        error
          ? []
          : [
              ...arg.ids.map((id: number) => ({
                type: TagTypes.PcRawMaterial,
                id,
              })),
              { type: TagTypes.PcRawMaterial, id: "LIST" },
            ],
    }),
  }),
});

export const {
  useGetPcRawMaterialCommonInfoQuery,
  useGetPcRawMaterialFilteredPagedQuery,
  useGetPcRawMaterialQuery,
  useAddPcRawMaterialMutation,
  useEditPcRawMaterialMutation,
  useScrapPcRawMaterialQuery,
  useLazyScrapPcRawMaterialQuery,
  useDeletePcRawMaterialMutation,
  useDeleteMultiPcRawMaterialsMutation,
} = pcRawMaterialApi;
